import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import "./AgreementTemplateStudio.css";


const API_ROOT = "/api/agreements";


function getAuthHeaders() {
  const headers = {
    "Content-Type": "application/json",
  };

  const token =
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token");

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}


async function apiFetch(path, options = {}) {
  const response = await fetch(`${API_ROOT}${path}`, {
    credentials: "include",
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });

  let payload = null;

  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    let message =
      payload?.detail?.message ||
      payload?.detail ||
      payload?.message ||
      `Request failed with HTTP ${response.status}`;

    if (typeof message !== "string") {
      message = JSON.stringify(message);
    }

    throw new Error(message);
  }

  return payload;
}


const EMPTY_TEMPLATE = {
  template_key: "",
  name: "",
  description: "",
  category: "employment",
  agreement_body: "",
  requires_signature: true,

  fields: [],
  attachment_requirements: [],

  assigned_roles: [],
  assigned_user_ids: [],
  assigned_contractor_ids: [],
  assigned_site_ids: [],
  assigned_client_ids: [],

  issuing_entity_type: "tenant",
  issuing_entity_id: "",
};


const FIELD_TYPES = [
  "text",
  "textarea",
  "email",
  "phone",
  "date",
  "number",
  "checkbox",
  "select",
  "radio",
  "declaration",
];


const CATEGORIES = [
  "employment",
  "contractor",
  "indemnity",
  "confidentiality",
  "policy",
  "induction",
  "privacy",
  "flexibility",
  "declaration",
  "other",
];


function clone(value) {
  return JSON.parse(JSON.stringify(value));
}


function csvToArray(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}


function arrayToCsv(value) {
  return Array.isArray(value) ? value.join(", ") : "";
}


function cleanTemplateForEdit(template) {
  return {
    template_key: template.template_key || "",
    name: template.name || "",
    description: template.description || "",
    category: template.category || "other",
    agreement_body: template.agreement_body || "",
    requires_signature:
      template.requires_signature !== false,

    fields: clone(template.fields || []),

    attachment_requirements: clone(
      template.attachment_requirements || []
    ),

    assigned_roles: clone(
      template.assigned_roles || []
    ),

    assigned_user_ids: clone(
      template.assigned_user_ids || []
    ),

    assigned_contractor_ids: clone(
      template.assigned_contractor_ids || []
    ),

    assigned_site_ids: clone(
      template.assigned_site_ids || []
    ),

    assigned_client_ids: clone(
      template.assigned_client_ids || []
    ),

    issuing_entity_type:
      template.issuing_entity_type || "tenant",

    issuing_entity_id:
      template.issuing_entity_id || "",
  };
}


function StatusBadge({ status }) {
  const normalized = String(status || "unknown")
    .toLowerCase();

  return (
    <span
      className={`rx-agreement-status rx-agreement-status--${normalized}`}
    >
      {normalized.toUpperCase()}
    </span>
  );
}


function Modal({
  title,
  children,
  onClose,
  wide = false,
}) {
  return (
    <div
      className="rx-agreement-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={
          wide
            ? "rx-agreement-modal rx-agreement-modal--wide"
            : "rx-agreement-modal"
        }
        role="dialog"
        aria-modal="true"
      >
        <div className="rx-agreement-modal__header">
          <h2>{title}</h2>

          <button
            type="button"
            className="rx-button rx-button--ghost"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="rx-agreement-modal__body">
          {children}
        </div>
      </div>
    </div>
  );
}


function ConfirmDialog({
  title,
  message,
  confirmText,
  destructive = false,
  onConfirm,
  onCancel,
  busy,
}) {
  return (
    <Modal
      title={title}
      onClose={busy ? () => {} : onCancel}
    >
      <p className="rx-confirm-message">
        {message}
      </p>

      <div className="rx-form-actions">
        <button
          type="button"
          className="rx-button rx-button--ghost"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>

        <button
          type="button"
          className={
            destructive
              ? "rx-button rx-button--danger"
              : "rx-button rx-button--primary"
          }
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? "Working..." : confirmText}
        </button>
      </div>
    </Modal>
  );
}


function TemplatePreview({ template }) {
  return (
    <div className="rx-preview">
      <div className="rx-preview__meta">
        <div>
          <strong>{template.name}</strong>
        </div>

        <div>
          Key: {template.template_key}
        </div>

        <div>
          Version: {template.version || 1}
        </div>

        <div>
          <StatusBadge status={template.status} />
        </div>
      </div>

      <div className="rx-preview__paper">
        <div className="rx-preview__body">
          {String(
            template.agreement_body || ""
          )
            .split("\n")
            .map((line, index) => (
              <p key={index}>
                {line || "\u00A0"}
              </p>
            ))}
        </div>

        {(template.fields || []).length > 0 && (
          <section className="rx-preview__section">
            <h3>Information to complete</h3>

            {template.fields.map((field) => (
              <div
                key={field.key}
                className="rx-preview-field"
              >
                <strong>{field.label}</strong>

                {field.required && (
                  <span className="rx-required">
                    Required
                  </span>
                )}

                <div className="rx-preview-input">
                  {field.field_type}
                </div>
              </div>
            ))}
          </section>
        )}

        {(template.attachment_requirements || [])
          .length > 0 && (
          <section className="rx-preview__section">
            <h3>Required documents</h3>

            {template.attachment_requirements.map(
              (item) => (
                <div
                  key={item.key}
                  className="rx-preview-attachment"
                >
                  <strong>{item.label}</strong>

                  {item.required && (
                    <span className="rx-required">
                      Required
                    </span>
                  )}
                </div>
              )
            )}
          </section>
        )}

        {template.requires_signature && (
          <section className="rx-preview__section">
            <h3>Electronic signature</h3>

            <div className="rx-signature-placeholder">
              Signer signature
            </div>
          </section>
        )}
      </div>
    </div>
  );
}


function FieldsEditor({
  fields,
  onChange,
}) {
  const addField = () => {
    onChange([
      ...fields,
      {
        key: `field_${fields.length + 1}`,
        label: "New Field",
        field_type: "text",
        required: false,
        placeholder: "",
        help_text: "",
        options: [],
        validation: {},
      },
    ]);
  };

  const updateField = (
    index,
    key,
    value
  ) => {
    const copy = clone(fields);

    copy[index][key] = value;

    onChange(copy);
  };

  const removeField = (index) => {
    onChange(
      fields.filter(
        (_, itemIndex) =>
          itemIndex !== index
      )
    );
  };

  return (
    <div>
      <div className="rx-section-heading">
        <div>
          <h3>Form fields</h3>
          <p>
            Information the recipient must complete
            as part of the agreement.
          </p>
        </div>

        <button
          type="button"
          className="rx-button rx-button--secondary"
          onClick={addField}
        >
          + Add field
        </button>
      </div>

      {fields.length === 0 && (
        <div className="rx-empty-small">
          No form fields configured.
        </div>
      )}

      {fields.map((field, index) => (
        <div
          className="rx-builder-row"
          key={`${field.key}-${index}`}
        >
          <div className="rx-grid rx-grid--4">
            <label>
              Field key
              <input
                value={field.key || ""}
                onChange={(event) =>
                  updateField(
                    index,
                    "key",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              Label
              <input
                value={field.label || ""}
                onChange={(event) =>
                  updateField(
                    index,
                    "label",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              Type
              <select
                value={
                  field.field_type || "text"
                }
                onChange={(event) =>
                  updateField(
                    index,
                    "field_type",
                    event.target.value
                  )
                }
              >
                {FIELD_TYPES.map((type) => (
                  <option
                    value={type}
                    key={type}
                  >
                    {type}
                  </option>
                ))}
              </select>
            </label>

            <label className="rx-checkbox-label">
              <input
                type="checkbox"
                checked={Boolean(
                  field.required
                )}
                onChange={(event) =>
                  updateField(
                    index,
                    "required",
                    event.target.checked
                  )
                }
              />
              Required
            </label>
          </div>

          <div className="rx-grid rx-grid--2">
            <label>
              Placeholder
              <input
                value={
                  field.placeholder || ""
                }
                onChange={(event) =>
                  updateField(
                    index,
                    "placeholder",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              Help text
              <input
                value={
                  field.help_text || ""
                }
                onChange={(event) =>
                  updateField(
                    index,
                    "help_text",
                    event.target.value
                  )
                }
              />
            </label>
          </div>

          {["select", "radio"].includes(
            field.field_type
          ) && (
            <label>
              Options — comma separated
              <input
                value={arrayToCsv(
                  field.options
                )}
                onChange={(event) =>
                  updateField(
                    index,
                    "options",
                    csvToArray(
                      event.target.value
                    )
                  )
                }
              />
            </label>
          )}

          <button
            type="button"
            className="rx-button rx-button--danger-link"
            onClick={() =>
              removeField(index)
            }
          >
            Remove field
          </button>
        </div>
      ))}
    </div>
  );
}


function AttachmentEditor({
  requirements,
  onChange,
}) {
  const addRequirement = () => {
    onChange([
      ...requirements,
      {
        key: `document_${
          requirements.length + 1
        }`,
        label: "Required Document",
        required: true,
        accepted_types: [
          "application/pdf",
          "image/jpeg",
          "image/png",
        ],
        description: "",
      },
    ]);
  };

  const updateRequirement = (
    index,
    key,
    value
  ) => {
    const copy = clone(requirements);

    copy[index][key] = value;

    onChange(copy);
  };

  const removeRequirement = (index) => {
    onChange(
      requirements.filter(
        (_, itemIndex) =>
          itemIndex !== index
      )
    );
  };

  return (
    <div>
      <div className="rx-section-heading">
        <div>
          <h3>Document requirements</h3>
          <p>
            Documents the recipient must upload
            before execution can complete.
          </p>
        </div>

        <button
          type="button"
          className="rx-button rx-button--secondary"
          onClick={addRequirement}
        >
          + Add document
        </button>
      </div>

      {requirements.length === 0 && (
        <div className="rx-empty-small">
          No attachment requirements configured.
        </div>
      )}

      {requirements.map(
        (requirement, index) => (
          <div
            className="rx-builder-row"
            key={`${requirement.key}-${index}`}
          >
            <div className="rx-grid rx-grid--3">
              <label>
                Requirement key
                <input
                  value={
                    requirement.key || ""
                  }
                  onChange={(event) =>
                    updateRequirement(
                      index,
                      "key",
                      event.target.value
                    )
                  }
                />
              </label>

              <label>
                Label
                <input
                  value={
                    requirement.label || ""
                  }
                  onChange={(event) =>
                    updateRequirement(
                      index,
                      "label",
                      event.target.value
                    )
                  }
                />
              </label>

              <label className="rx-checkbox-label">
                <input
                  type="checkbox"
                  checked={Boolean(
                    requirement.required
                  )}
                  onChange={(event) =>
                    updateRequirement(
                      index,
                      "required",
                      event.target.checked
                    )
                  }
                />
                Required
              </label>
            </div>

            <label>
              Accepted MIME types — comma separated
              <input
                value={arrayToCsv(
                  requirement.accepted_types
                )}
                onChange={(event) =>
                  updateRequirement(
                    index,
                    "accepted_types",
                    csvToArray(
                      event.target.value
                    )
                  )
                }
              />
            </label>

            <label>
              Description
              <input
                value={
                  requirement.description || ""
                }
                onChange={(event) =>
                  updateRequirement(
                    index,
                    "description",
                    event.target.value
                  )
                }
              />
            </label>

            <button
              type="button"
              className="rx-button rx-button--danger-link"
              onClick={() =>
                removeRequirement(index)
              }
            >
              Remove requirement
            </button>
          </div>
        )
      )}
    </div>
  );
}



function ReferenceMultiSelect({
  label,
  endpoint,
  values,
  onChange,
  placeholder,
}) {
  const [query, setQuery] =
    useState("");

  const [results, setResults] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [open, setOpen] =
    useState(false);

  const [labels, setLabels] =
    useState({});


  const search = useCallback(
    async (searchQuery = "") => {
      setLoading(true);

      try {
        const params =
          new URLSearchParams();

        if (searchQuery.trim()) {
          params.set(
            "q",
            searchQuery.trim()
          );
        }

        params.set("limit", "30");

        const payload =
          await apiFetch(
            `${endpoint}?${params.toString()}`
          );

        const items =
          payload?.items || [];

        setResults(items);

        setLabels((current) => {
          const next = {
            ...current,
          };

          items.forEach((item) => {
            next[item.id] =
              item.label || item.id;
          });

          return next;
        });
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [endpoint]
  );


  useEffect(() => {
    if (!open) {
      return;
    }

    const timer =
      window.setTimeout(
        () => search(query),
        250
      );

    return () =>
      window.clearTimeout(timer);

  }, [
    query,
    open,
    search,
  ]);


  const add = (item) => {
    if (!values.includes(item.id)) {
      onChange([
        ...values,
        item.id,
      ]);
    }

    setLabels((current) => ({
      ...current,
      [item.id]:
        item.label || item.id,
    }));

    setQuery("");
    setOpen(false);
  };


  const remove = (id) => {
    onChange(
      values.filter(
        (value) => value !== id
      )
    );
  };


  return (
    <div className="rx-reference-field">
      <label>
        {label}

        <div className="rx-reference-box">
          <div className="rx-reference-chips">
            {values.map((id) => (
              <span
                className="rx-reference-chip"
                key={id}
              >
                <span>
                  {labels[id] || id}
                </span>

                <button
                  type="button"
                  onClick={() =>
                    remove(id)
                  }
                  aria-label={`Remove ${
                    labels[id] || id
                  }`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          <input
            value={query}
            placeholder={placeholder}
            onFocus={() => {
              setOpen(true);

              if (
                results.length === 0
              ) {
                search("");
              }
            }}
            onChange={(event) => {
              setQuery(
                event.target.value
              );

              setOpen(true);
            }}
          />

          {open && (
            <div className="rx-reference-results">
              {loading && (
                <div className="rx-reference-message">
                  Searching...
                </div>
              )}

              {!loading &&
                results.length === 0 && (
                <div className="rx-reference-message">
                  No matching records
                </div>
              )}

              {!loading &&
                results.map((item) => (
                  <button
                    type="button"
                    className="rx-reference-result"
                    key={item.id}
                    onClick={() =>
                      add(item)
                    }
                  >
                    <strong>
                      {item.label}
                    </strong>

                    {item.email && (
                      <span>
                        {item.email}
                      </span>
                    )}

                    {item.role && (
                      <span>
                        {item.role}
                      </span>
                    )}

                    {item.site_code && (
                      <span>
                        {item.site_code}
                      </span>
                    )}
                  </button>
                ))}
            </div>
          )}
        </div>
      </label>
    </div>
  );
}


function AssignmentImpact({
  impact,
  onClose,
}) {
  return (
    <Modal
      title={`Assignment Preview — ${
        impact?.template?.name || ""
      }`}
      wide
      onClose={onClose}
    >
      <div className="rx-impact-summary">
        <div className="rx-stat-card">
          <span>
            Users evaluated
          </span>
          <strong>
            {impact.users_evaluated}
          </strong>
        </div>

        <div className="rx-stat-card">
          <span>
            Would apply
          </span>
          <strong>
            {impact.matched_count}
          </strong>
        </div>

        <div className="rx-stat-card">
          <span>
            Would not apply
          </span>
          <strong>
            {impact.not_matched_count}
          </strong>
        </div>
      </div>

      <div className="rx-impact-columns">
        <section>
          <h3>
            Would apply
          </h3>

          {impact.matched.length ===
            0 && (
            <div className="rx-empty-small">
              No current users match.
            </div>
          )}

          {impact.matched.map(
            (item) => (
              <div
                className="rx-impact-person"
                key={item.user.id}
              >
                <strong>
                  {item.user.label}
                </strong>

                <span>
                  {item.user.email || ""}
                </span>

                <small>
                  {item.reason}
                </small>
              </div>
            )
          )}
        </section>

        <section>
          <h3>
            Would not apply
          </h3>

          {impact.not_matched.length ===
            0 && (
            <div className="rx-empty-small">
              No non-matching users.
            </div>
          )}

          {impact.not_matched.map(
            (item) => (
              <div
                className="rx-impact-person rx-impact-person--muted"
                key={item.user.id}
              >
                <strong>
                  {item.user.label}
                </strong>

                <span>
                  {item.user.email || ""}
                </span>

                <small>
                  {item.reason}
                </small>
              </div>
            )
          )}
        </section>
      </div>
    </Modal>
  );
}


function TemplateEditor({
  initialTemplate,
  mode,
  busy,
  onSave,
  onCancel,
  onPreview,
}) {
  const [form, setForm] = useState(
    cleanTemplateForEdit(
      initialTemplate || EMPTY_TEMPLATE
    )
  );

  const setValue = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const submit = (event) => {
    event.preventDefault();

    const payload = {
      ...form,
      template_key:
        form.template_key
          .trim()
          .toLowerCase()
          .replace(/\s+/g, "_"),

      name: form.name.trim(),

      category: form.category.trim(),

      agreement_body:
        form.agreement_body.trim(),

      description:
        form.description?.trim() || null,

      issuing_entity_id:
        form.issuing_entity_id?.trim() ||
        null,
    };

    if (mode === "edit") {
      delete payload.template_key;
    }

    onSave(payload);
  };

  return (
    <form
      className="rx-template-editor"
      onSubmit={submit}
    >
      <section className="rx-editor-section">
        <h3>Template identity</h3>

        <div className="rx-grid rx-grid--3">
          <label>
            Template name
            <input
              required
              minLength={2}
              value={form.name}
              onChange={(event) =>
                setValue(
                  "name",
                  event.target.value
                )
              }
            />
          </label>

          <label>
            Template key
            <input
              required
              disabled={mode === "edit"}
              value={form.template_key}
              placeholder="contractor_personnel_indemnity"
              onChange={(event) =>
                setValue(
                  "template_key",
                  event.target.value
                )
              }
            />
          </label>

          <label>
            Category
            <select
              value={form.category}
              onChange={(event) =>
                setValue(
                  "category",
                  event.target.value
                )
              }
            >
              {CATEGORIES.map(
                (category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {category}
                  </option>
                )
              )}
            </select>
          </label>
        </div>

        <label>
          Description
          <textarea
            rows={2}
            value={form.description || ""}
            onChange={(event) =>
              setValue(
                "description",
                event.target.value
              )
            }
          />
        </label>
      </section>

      <section className="rx-editor-section">
        <div className="rx-section-heading">
          <div>
            <h3>Agreement wording</h3>
            <p>
              This wording becomes immutable when
              the version is activated.
            </p>
          </div>
        </div>

        <textarea
          className="rx-agreement-body-editor"
          required
          rows={20}
          value={form.agreement_body}
          placeholder={
            "Enter the full agreement wording here..."
          }
          onChange={(event) =>
            setValue(
              "agreement_body",
              event.target.value
            )
          }
        />
      </section>

      <section className="rx-editor-section">
        <FieldsEditor
          fields={form.fields}
          onChange={(value) =>
            setValue("fields", value)
          }
        />
      </section>

      <section className="rx-editor-section">
        <AttachmentEditor
          requirements={
            form.attachment_requirements
          }
          onChange={(value) =>
            setValue(
              "attachment_requirements",
              value
            )
          }
        />
      </section>

      <section className="rx-editor-section">
        <h3>Execution requirements</h3>

        <label className="rx-checkbox-label rx-checkbox-label--large">
          <input
            type="checkbox"
            checked={form.requires_signature}
            onChange={(event) =>
              setValue(
                "requires_signature",
                event.target.checked
              )
            }
          />

          Electronic signature required
        </label>
      </section>

      <section className="rx-editor-section">
        <h3>
          Applicability
        </h3>

        <p className="rx-help-text">
          Empty dimensions are unrestricted.
          Values within a dimension use OR.
          Populated dimensions combine using AND.
        </p>

        <div className="rx-grid rx-grid--2">
          <label>
            Roles
            <input
              value={arrayToCsv(
                form.assigned_roles
              )}
              placeholder="security_officer, supervisor"
              onChange={(event) =>
                setValue(
                  "assigned_roles",
                  csvToArray(
                    event.target.value
                  )
                )
              }
            />
          </label>

          <ReferenceMultiSelect
            label="Specific Rapid X users"
            endpoint="/admin/reference/users"
            values={
              form.assigned_user_ids
            }
            onChange={(value) =>
              setValue(
                "assigned_user_ids",
                value
              )
            }
            placeholder="Search staff..."
          />

          <ReferenceMultiSelect
            label="Contractors"
            endpoint="/admin/reference/contractors"
            values={
              form.assigned_contractor_ids
            }
            onChange={(value) =>
              setValue(
                "assigned_contractor_ids",
                value
              )
            }
            placeholder="Search contractors..."
          />

          <ReferenceMultiSelect
            label="Sites"
            endpoint="/admin/reference/sites"
            values={
              form.assigned_site_ids
            }
            onChange={(value) =>
              setValue(
                "assigned_site_ids",
                value
              )
            }
            placeholder="Search sites..."
          />

          <ReferenceMultiSelect
            label="Clients"
            endpoint="/admin/reference/clients"
            values={
              form.assigned_client_ids
            }
            onChange={(value) =>
              setValue(
                "assigned_client_ids",
                value
              )
            }
            placeholder="Search clients..."
          />
        </div>
      </section>

      <section className="rx-editor-section">
        <h3>Issuing entity</h3>

        <div className="rx-grid rx-grid--2">
          <label>
            Entity type
            <select
              value={
                form.issuing_entity_type
              }
              onChange={(event) =>
                setValue(
                  "issuing_entity_type",
                  event.target.value
                )
              }
            >
              <option value="tenant">
                Tenant / Principal
              </option>

              <option value="contractor">
                Contractor
              </option>

              <option value="client">
                Client
              </option>

              <option value="other">
                Other
              </option>
            </select>
          </label>

          <label>
            Entity ID
            <input
              value={
                form.issuing_entity_id || ""
              }
              placeholder={
                form.issuing_entity_type ===
                "tenant"
                  ? "Leave blank for current tenant"
                  : "Entity identifier"
              }
              onChange={(event) =>
                setValue(
                  "issuing_entity_id",
                  event.target.value
                )
              }
            />
          </label>
        </div>
      </section>

      <div className="rx-form-actions">
        <button
          type="button"
          className="rx-button rx-button--ghost"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>

        <button
          type="button"
          className="rx-button rx-button--secondary"
          onClick={() =>
            onPreview({
              ...initialTemplate,
              ...form,
              status:
                initialTemplate?.status ||
                "draft",
            })
          }
          disabled={busy}
        >
          Preview
        </button>

        <button
          type="submit"
          className="rx-button rx-button--primary"
          disabled={busy}
        >
          {busy
            ? "Saving..."
            : mode === "create"
            ? "Create draft"
            : "Save draft"}
        </button>
      </div>
    </form>
  );
}


export default function AgreementTemplateStudio() {
  const [templates, setTemplates] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const [notice, setNotice] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("");

  const [categoryFilter, setCategoryFilter] =
    useState("");

  const [editor, setEditor] =
    useState(null);

  const [preview, setPreview] =
    useState(null);

  const [confirm, setConfirm] =
    useState(null);

  const [history, setHistory] =
    useState(null);

  const [assignmentImpact, setAssignmentImpact] =
    useState(null);


  const loadTemplates = useCallback(
    async () => {
      setLoading(true);
      setError("");

      try {
        const params = new URLSearchParams();

        if (statusFilter) {
          params.set(
            "status",
            statusFilter
          );
        }

        if (categoryFilter) {
          params.set(
            "category",
            categoryFilter
          );
        }

        const query = params.toString();

        const payload = await apiFetch(
          `/templates${
            query ? `?${query}` : ""
          }`
        );

        setTemplates(
          payload?.templates || []
        );
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    },
    [
      statusFilter,
      categoryFilter,
    ]
  );


  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);


  const groupedCount = useMemo(() => {
    const counts = {
      draft: 0,
      active: 0,
      retired: 0,
    };

    templates.forEach((template) => {
      const status =
        String(template.status || "")
          .toLowerCase();

      if (
        Object.prototype.hasOwnProperty.call(
          counts,
          status
        )
      ) {
        counts[status] += 1;
      }
    });

    return counts;
  }, [templates]);


  const createTemplate = async (
    payload
  ) => {
    setBusy(true);
    setError("");

    try {
      await apiFetch("/templates", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      setEditor(null);

      setNotice(
        "Draft agreement template created."
      );

      await loadTemplates();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };


  const updateTemplate = async (
    template,
    payload
  ) => {
    setBusy(true);
    setError("");

    try {
      await apiFetch(
        `/templates/${
          template.id || template._id
        }`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        }
      );

      setEditor(null);

      setNotice(
        "Draft agreement template updated."
      );

      await loadTemplates();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };


  const deleteTemplate = async (
    template
  ) => {
    setBusy(true);
    setError("");

    try {
      await apiFetch(
        `/templates/${
          template.id || template._id
        }`,
        {
          method: "DELETE",
        }
      );

      setConfirm(null);

      setNotice(
        "Draft template deleted."
      );

      await loadTemplates();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };


  const activateTemplate = async (
    template
  ) => {
    setBusy(true);
    setError("");

    try {
      const result = await apiFetch(
        `/templates/${
          template.id || template._id
        }/activate`,
        {
          method: "POST",
        }
      );

      setConfirm(null);

      setNotice(
        `Version ${
          result?.template?.version ||
          template.version
        } activated and frozen.`
      );

      await loadTemplates();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };


  const createNewVersion = async (
    template
  ) => {
    setBusy(true);
    setError("");

    try {
      const result = await apiFetch(
        `/templates/${
          template.id || template._id
        }/new-version`,
        {
          method: "POST",
        }
      );

      setNotice(
        `Draft version ${
          result?.new_version
        } created from version ${
          result?.source_version
        }.`
      );

      const draft =
        result?.template || null;

      await loadTemplates();

      if (draft) {
        setEditor({
          mode: "edit",
          template: draft,
        });
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };


  const previewAssignmentImpact = async (
    template
  ) => {
    setBusy(true);
    setError("");

    try {
      const result = await apiFetch(
        `/admin/templates/${
          template.id || template._id
        }/assignment-impact`
      );

      setAssignmentImpact(result);
    } catch (requestError) {
      setError(
        requestError.message
      );
    } finally {
      setBusy(false);
    }
  };


  const viewHistory = async (
    template
  ) => {
    setBusy(true);
    setError("");

    try {
      const result = await apiFetch(
        `/template-keys/${
          template.template_key
        }/versions`
      );

      setHistory(result);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  };


  return (
    <main className="rx-agreements-page">
      <div className="rx-page-heading">
        <div>
          <div className="rx-eyebrow">
            RAPID X COMPLIANCE
          </div>

          <h1>
            Agreement Template Studio
          </h1>

          <p>
            Build, review and version-control
            agreements before they are issued
            for electronic execution.
          </p>
        </div>

        <button
          type="button"
          className="rx-button rx-button--primary"
          onClick={() =>
            setEditor({
              mode: "create",
              template: clone(
                EMPTY_TEMPLATE
              ),
            })
          }
        >
          + New agreement template
        </button>
      </div>

      <div className="rx-safety-banner">
        <strong>
          Template management only.
        </strong>

        Agreement issuing, electronic
        signature execution and delivery are
        not enabled in this stage.
      </div>

      {error && (
        <div className="rx-alert rx-alert--error">
          {error}
        </div>
      )}

      {notice && (
        <div className="rx-alert rx-alert--success">
          <span>{notice}</span>

          <button
            type="button"
            onClick={() =>
              setNotice("")
            }
          >
            ×
          </button>
        </div>
      )}

      <div className="rx-stat-grid">
        <div className="rx-stat-card">
          <span>Draft</span>
          <strong>
            {groupedCount.draft}
          </strong>
        </div>

        <div className="rx-stat-card">
          <span>Active</span>
          <strong>
            {groupedCount.active}
          </strong>
        </div>

        <div className="rx-stat-card">
          <span>Retired</span>
          <strong>
            {groupedCount.retired}
          </strong>
        </div>

        <div className="rx-stat-card">
          <span>Total shown</span>
          <strong>
            {templates.length}
          </strong>
        </div>
      </div>

      <section className="rx-register">
        <div className="rx-register__toolbar">
          <div>
            <h2>Template Register</h2>

            <p>
              Activated versions are immutable.
              Amendments require a new version.
            </p>
          </div>

          <div className="rx-filter-group">
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All statuses
              </option>

              <option value="draft">
                Draft
              </option>

              <option value="active">
                Active
              </option>

              <option value="retired">
                Retired
              </option>
            </select>

            <select
              value={categoryFilter}
              onChange={(event) =>
                setCategoryFilter(
                  event.target.value
                )
              }
            >
              <option value="">
                All categories
              </option>

              {CATEGORIES.map(
                (category) => (
                  <option
                    key={category}
                    value={category}
                  >
                    {category}
                  </option>
                )
              )}
            </select>

            <button
              type="button"
              className="rx-button rx-button--ghost"
              onClick={loadTemplates}
            >
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="rx-loading">
            Loading agreement templates...
          </div>
        ) : templates.length === 0 ? (
          <div className="rx-empty">
            <h3>
              No agreement templates yet
            </h3>

            <p>
              Create the first draft template
              when you are ready.
            </p>
          </div>
        ) : (
          <div className="rx-table-wrap">
            <table className="rx-template-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Template key</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Signature</th>
                  <th>Applicability</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {templates.map(
                  (template) => {
                    const status =
                      String(
                        template.status || ""
                      ).toLowerCase();

                    const id =
                      template.id ||
                      template._id;

                    const applicability =
                      [
                        template
                          .assigned_roles
                          ?.length
                          ? `${
                              template
                                .assigned_roles
                                .length
                            } role(s)`
                          : null,

                        template
                          .assigned_user_ids
                          ?.length
                          ? `${
                              template
                                .assigned_user_ids
                                .length
                            } user(s)`
                          : null,

                        template
                          .assigned_contractor_ids
                          ?.length
                          ? `${
                              template
                                .assigned_contractor_ids
                                .length
                            } contractor(s)`
                          : null,

                        template
                          .assigned_site_ids
                          ?.length
                          ? `${
                              template
                                .assigned_site_ids
                                .length
                            } site(s)`
                          : null,

                        template
                          .assigned_client_ids
                          ?.length
                          ? `${
                              template
                                .assigned_client_ids
                                .length
                            } client(s)`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(", ");

                    return (
                      <tr key={id}>
                        <td>
                          <strong>
                            {template.name}
                          </strong>

                          {template.description && (
                            <small>
                              {
                                template.description
                              }
                            </small>
                          )}
                        </td>

                        <td>
                          {template.category}
                        </td>

                        <td>
                          <code>
                            {
                              template.template_key
                            }
                          </code>
                        </td>

                        <td>
                          v{template.version}
                        </td>

                        <td>
                          <StatusBadge
                            status={status}
                          />
                        </td>

                        <td>
                          {template.requires_signature
                            ? "Required"
                            : "Not required"}
                        </td>

                        <td>
                          {applicability ||
                            "General"}
                        </td>

                        <td>
                          <div className="rx-actions">
                            <button
                              type="button"
                              className="rx-link-button"
                              onClick={() =>
                                setPreview(
                                  template
                                )
                              }
                            >
                              Preview
                            </button>

                            {status ===
                              "draft" && (
                              <>
                                <button
                                  type="button"
                                  className="rx-link-button"
                                  onClick={() =>
                                    setEditor({
                                      mode: "edit",
                                      template,
                                    })
                                  }
                                >
                                  Edit
                                </button>

                                <button
                                  type="button"
                                  className="rx-link-button"
                                  onClick={() =>
                                    setConfirm({
                                      type:
                                        "activate",
                                      template,
                                    })
                                  }
                                >
                                  Activate
                                </button>

                                <button
                                  type="button"
                                  className="rx-link-button rx-link-button--danger"
                                  onClick={() =>
                                    setConfirm({
                                      type:
                                        "delete",
                                      template,
                                    })
                                  }
                                >
                                  Delete
                                </button>
                              </>
                            )}

                            {status ===
                              "active" && (
                              <button
                                type="button"
                                className="rx-link-button"
                                onClick={() =>
                                  createNewVersion(
                                    template
                                  )
                                }
                                disabled={busy}
                              >
                                New version
                              </button>
                            )}

                            {(status ===
                              "active" ||
                              status ===
                                "retired") && (
                              <button
                                type="button"
                                className="rx-link-button"
                                onClick={() =>
                                  viewHistory(
                                    template
                                  )
                                }
                              >
                                History
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  }
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editor && (
        <Modal
          title={
            editor.mode === "create"
              ? "New Agreement Template"
              : `Edit ${
                  editor.template.name
                } — v${
                  editor.template.version
                }`
          }
          wide
          onClose={() =>
            !busy && setEditor(null)
          }
        >
          <TemplateEditor
            initialTemplate={
              editor.template
            }
            mode={editor.mode}
            busy={busy}
            onCancel={() =>
              setEditor(null)
            }
            onPreview={setPreview}
            onSave={(payload) =>
              editor.mode === "create"
                ? createTemplate(payload)
                : updateTemplate(
                    editor.template,
                    payload
                  )
            }
          />
        </Modal>
      )}

      {preview && (
        <Modal
          title="Agreement Preview"
          wide
          onClose={() =>
            setPreview(null)
          }
        >
          <TemplatePreview
            template={preview}
          />
        </Modal>
      )}

      {history && (
        <Modal
          title={`Version History — ${history.template_key}`}
          wide
          onClose={() =>
            setHistory(null)
          }
        >
          <div className="rx-version-summary">
            <div>
              Active version:
              <strong>
                {" "}
                {history.active_version
                  ? `v${history.active_version}`
                  : "None"}
              </strong>
            </div>

            <div>
              Draft version:
              <strong>
                {" "}
                {history.draft_version
                  ? `v${history.draft_version}`
                  : "None"}
              </strong>
            </div>
          </div>

          <div className="rx-version-list">
            {(history.versions || []).map(
              (version) => (
                <div
                  className="rx-version-card"
                  key={
                    version.id ||
                    version._id
                  }
                >
                  <div>
                    <strong>
                      v{version.version}
                    </strong>

                    <StatusBadge
                      status={
                        version.status
                      }
                    />
                  </div>

                  <div>
                    {version.name}
                  </div>

                  <div className="rx-version-dates">
                    Activated:{" "}
                    {version.activated_at ||
                      "—"}
                    <br />
                    Retired:{" "}
                    {version.retired_at ||
                      "—"}
                  </div>

                  <button
                    type="button"
                    className="rx-button rx-button--ghost"
                    onClick={() =>
                      setPreview(version)
                    }
                  >
                    View
                  </button>
                </div>
              )
            )}
          </div>
        </Modal>
      )}

      {assignmentImpact && (
        <AssignmentImpact
          impact={assignmentImpact}
          onClose={() =>
            setAssignmentImpact(null)
          }
        />
      )}

      {confirm?.type === "delete" && (
        <ConfirmDialog
          title="Delete draft template?"
          destructive
          confirmText="Delete draft"
          busy={busy}
          message={
            `Delete ${
              confirm.template.name
            } v${
              confirm.template.version
            }? This is allowed only because the version is still DRAFT.`
          }
          onCancel={() =>
            setConfirm(null)
          }
          onConfirm={() =>
            deleteTemplate(
              confirm.template
            )
          }
        />
      )}

      {confirm?.type ===
        "activate" && (
        <ConfirmDialog
          title="Activate and freeze template?"
          confirmText="Activate & freeze"
          busy={busy}
          message={
            `Activate ${
              confirm.template.name
            } v${
              confirm.template.version
            }? Once activated, this version becomes immutable. Future changes require a new version.`
          }
          onCancel={() =>
            setConfirm(null)
          }
          onConfirm={() =>
            activateTemplate(
              confirm.template
            )
          }
        />
      )}
    </main>
  );
}
